/**
 * Smart routing: scan issue text for keywords and assign team + suggested priority.
 * When multiple rules match, the highest-severity rule wins.
 */

const PRIORITY_RANK = { Critical: 4, High: 3, Medium: 2, Low: 1, Normal: 1 };

const ROUTING_RULES = [
  {
    id: "network-ops",
    keywords: ["no internet", "not connecting"],
    team: "Network Operations",
    routingPriority: "Critical",
  },
  {
    id: "network-security",
    keywords: ["vpn", "remote access", "tunnel"],
    team: "Network Security Team",
    routingPriority: "High",
  },
  {
    id: "security",
    keywords: ["firewall", "blocked", "policy"],
    team: "Security Team",
    routingPriority: "High",
  },
  {
    id: "cloud",
    keywords: ["cloud", "azure", "aws", "storage"],
    team: "Cloud Team",
    routingPriority: "Medium",
  },
  {
    id: "infrastructure",
    keywords: ["server", "virtual machine"],
    team: "Infrastructure Team",
    routingPriority: "Medium",
  },
  {
    id: "collaboration",
    keywords: ["email", "outlook"],
    team: "Collaboration Team",
    routingPriority: "Normal",
  },
];

const DEFAULT_ROUTE = {
  team: "General Support Team",
  routingPriority: "Normal",
  matchedRuleId: null,
};

export function rankPriority(p) {
  if (!p) return 0;
  return PRIORITY_RANK[p] ?? 0;
}

/** Normalize Normal to Low for comparison with form priorities */
export function normalizePriorityLabel(p) {
  return p === "Normal" ? "Low" : p;
}

/** Pick the stricter priority (Critical > High > Medium > Low). */
export function mergePriorities(userPriority, routingPriority) {
  const u = normalizePriorityLabel(userPriority);
  const r = normalizePriorityLabel(routingPriority);
  const rankU = rankPriority(u);
  const rankR = rankPriority(r);
  if (rankR > rankU) return routingPriority === "Normal" ? "Low" : routingPriority;
  return userPriority;
}

/**
 * @param {string} text — typically issue title + description
 * @returns {{ team: string, routingPriority: string, matchedRuleIds: string[] }}
 */
export function analyzeTicketRouting(text) {
  const t = (text || "").toLowerCase();
  const matches = [];
  for (const rule of ROUTING_RULES) {
    const hit = rule.keywords.some((kw) => t.includes(kw.toLowerCase()));
    if (hit) matches.push(rule);
  }
  if (matches.length === 0) {
    return {
      team: DEFAULT_ROUTE.team,
      routingPriority: DEFAULT_ROUTE.routingPriority,
      matchedRuleIds: [],
    };
  }
  let best = matches[0];
  for (let i = 1; i < matches.length; i++) {
    if (rankPriority(matches[i].routingPriority) > rankPriority(best.routingPriority)) {
      best = matches[i];
    }
  }
  return {
    team: best.team,
    routingPriority: best.routingPriority,
    matchedRuleIds: matches.map((m) => m.id),
  };
}
